import {Tooltip, Typography} from '@mui/material'
import {Box, Loading} from '@viptech/react-components'
import {useEffect, useMemo, useRef, useState} from 'react'
import useSnapshot from 'src/hooks/api/camera/useSnapshot'
import NoImage from 'src/images/noImage.svg'

type Props = {
  isLoading?: boolean
  data: HeatmapDetectionsType | undefined
  title: string
  isRefetching?: boolean
}

export type HeatmapDetectionsType = {
  cameraId: number
  width: number
  height: number
  objectName: string
  detections: Array<Array<number>>
}

type Point = {x: number; y: number; value: number}

const CANVAS_WIDTH = 750
const CANVAS_HEIGHT = 480
const GRADIENT_STRENGTH = 2
const MIN_RADIUS = 20

function HeatMapCard({isLoading, isRefetching, data, title}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const matrixRef = useRef<Float32Array[] | null>(null)
  const pointsRef = useRef<Point[]>([])

  const [loading, setLoading] = useState(false)

  const {data: snapshot, isError} = useSnapshot({
    cameraId: data?.cameraId ?? -1,
    enabled: !!data?.cameraId,
    retry: 0,
  })

  /* -------------------------------------------------------------------------- */
  /* Utils                                                                      */
  /* -------------------------------------------------------------------------- */

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max))

  const snapshotSrc = useMemo(() => {
    if (snapshot && !isError) return `data:image/jpeg;base64,${snapshot.time?.base64}`
    return NoImage
  }, [snapshot, isError])

  /* -------------------------------------------------------------------------- */
  /* Kernel (pré-calculado)                                                      */
  /* -------------------------------------------------------------------------- */

  const kernelCache = useRef<Map<number, Float32Array>>(new Map())

  function getKernel(radius: number) {
    if (kernelCache.current.has(radius)) {
      return kernelCache.current.get(radius)!
    }

    const size = radius * 2 + 1
    const kernel = new Float32Array(size * size)

    let i = 0
    for (let y = -radius; y <= radius; y++) {
      for (let x = -radius; x <= radius; x++) {
        const dist = Math.sqrt(x * x + y * y)
        kernel[i++] = dist <= radius ? Math.pow(1 - dist / radius, GRADIENT_STRENGTH) : 0
      }
    }

    kernelCache.current.set(radius, kernel)
    return kernel
  }

  /* -------------------------------------------------------------------------- */
  /* Heatmap calculation (RODA UMA VEZ POR DATA)                                 */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    if (!data || !canvasRef.current) return

    setLoading(true)

    const matrix = Array.from({length: data.height}, () => new Float32Array(data.width))
    const canvas = canvasRef.current

    for (const box of data.detections) {
      const [startX, startY, endX, endY] = box

      const cx = clamp(Math.floor((startX + endX) / 2), 0, canvas.width - 1)
      const cy = clamp(Math.floor((startY + endY) / 2), 0, canvas.height - 1)

      const radius = Math.max(MIN_RADIUS, Math.floor(Math.max(endX - startX, endY - startY) / 2))
      const kernel = getKernel(radius)

      let k = 0
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const value = kernel[k++]
          if (!value) continue

          const x = cx + dx
          const y = cy + dy

          if (x >= 0 && x < data.width && y >= 0 && y < data.height) {
            matrix[y][x] += value
          }
        }
      }
    }

    matrixRef.current = matrix

    /* Extrai pontos + min/max */
    let min = Infinity
    let max = -Infinity
    const points: Point[] = []

    for (let y = 0; y < matrix.length; y++) {
      for (let x = 0; x < matrix[0].length; x++) {
        const v = matrix[y][x]
        if (v > 0) {
          points.push({x, y, value: v})
          min = Math.min(min, v)
          max = Math.max(max, v)
        }
      }
    }

    pointsRef.current = points

    rangeRef.current = {min, max}
    drawCanvas(snapshotSrc)
    setLoading(false)
  }, [data]) // 🔥 snapshot NÃO dispara recálculo

  /* -------------------------------------------------------------------------- */
  /* Drawing                                                                    */
  /* -------------------------------------------------------------------------- */

  const rangeRef = useRef<{min: number; max: number}>({min: 0, max: 1})

  function drawCanvas(bgSrc: string) {
    const canvas = canvasRef.current
    const matrix = matrixRef.current
    if (!canvas || !matrix) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const {min, max} = rangeRef.current

    const bg = new Image()
    bg.src = bgSrc

    bg.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(bg, 0, 0, canvas.width, canvas.height)

      for (const {x, y, value} of pointsRef.current) {
        const n = (value - min) / (max - min || 1)
        const hue = 240 - n * 240
        const alpha = 0.3 + n * 0.7

        ctx.fillStyle = `hsla(${hue}, 100%, 50%, ${alpha})`
        ctx.fillRect(x, y, 1, 1)
      }
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Redraw background when snapshot changes                                     */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    if (!matrixRef.current || !pointsRef.current.length) return
    drawCanvas(snapshotSrc)
  }, [snapshotSrc])

  /* -------------------------------------------------------------------------- */
  /* Tooltip (registrado UMA vez)                                                */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const canvas = canvasRef.current
    const tooltip = tooltipRef.current
    if (!canvas || !tooltip) return

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      const x = Math.floor(e.clientX - rect.left)
      const y = Math.floor(e.clientY - rect.top)

      const found = pointsRef.current.find((p) => Math.abs(p.x - x) <= 1 && Math.abs(p.y - y) <= 1)

      if (found) {
        tooltip.textContent = `Intensidade: ${found.value.toFixed(2)}`
        tooltip.style.left = `${e.clientX + 10}px`
        tooltip.style.top = `${e.clientY - 10}px`
        tooltip.style.visibility = 'visible'
      } else {
        tooltip.style.visibility = 'hidden'
      }
    }

    canvas.addEventListener('mousemove', onMove)
    canvas.addEventListener('mouseleave', () => (tooltip.style.visibility = 'hidden'))

    return () => {
      canvas.removeEventListener('mousemove', onMove)
    }
  }, [])

  if (!data) return null

  return (
    <Box backgroundColor='#F5F5F5' borderRadius='16px' position='relative' width='100%'>
      <Box display='flex' direction='column' padding='42px 0' rowGap='10px' align='center'>
        <Typography fontWeight={700} fontSize='16px' color='#0F4C81'>
          {title}
        </Typography>

        {(isLoading || loading || isRefetching) && (
          <Tooltip title='Buscando atualizações'>
            <Box position='absolute' right='24px'>
              <Loading size='20px' />
            </Box>
          </Tooltip>
        )}

        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} />

        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            visibility: 'hidden',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        />
      </Box>
    </Box>
  )
}

export default HeatMapCard
